import { getDirectoryHandleCaseInsensitive } from '@/features/project-disk/fs'
import { appendMaterialResourceMappingEntry } from '@/features/material-library/resourceMapping'
import { buildProjectResourceObjectName, uploadMaterialFiles } from '@/features/material-library/upload'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import {
  buildDefaultVirtualResourceRoot,
  getTopFolderRuleBySegment,
  resolveVirtualResourcePathMeta,
  syncVirtualResourcePathMetadata,
} from '@/features/virtual-resource/rules'
import type DataFileMap from '@/model/Resource/DataFileMap'
import type MainProject from '@/model/MainProject'

const GRAPHIC_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'bmp', 'gif', 'svg', 'oaf', 'oui'])
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'])
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'mov', 'm4v'])
const FONT_EXTENSIONS = new Set(['ttf', 'otf', 'woff', 'woff2'])

type DiskResourceFile = {
  category: ResourceCategoryKey
  folderSegments: string[]
  fileName: string
  file: File
}

function getExtension(name: string): string {
  return name.includes('.') ? name.split('.').pop()!.toLowerCase() : ''
}

function isRemoteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim())
}

async function tryGetDirectory(
  parent: FileSystemDirectoryHandle,
  name: string,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    return await getDirectoryHandleCaseInsensitive(parent, name)
  } catch {
    return null
  }
}

function isAllowedFile(category: ResourceCategoryKey, fileName: string): boolean {
  const ext = getExtension(fileName)
  if (category === 'graphic') return GRAPHIC_EXTENSIONS.has(ext)
  if (category === 'audio') return AUDIO_EXTENSIONS.has(ext)
  if (category === 'video') return VIDEO_EXTENSIONS.has(ext)
  return FONT_EXTENSIONS.has(ext)
}

function resolveFolderSegments(category: ResourceCategoryKey, diskSegments: string[]): string[] {
  if (category === 'font') return ['文件', ...diskSegments]
  if (category === 'video') return ['视频', ...diskSegments]
  const [top, ...rest] = diskSegments
  const rule = getTopFolderRuleBySegment(category, top ?? '')
  if (rule) return [rule.name, ...rest]
  if (category === 'graphic') return ['其他', ...diskSegments]
  return diskSegments
}

function findLeafByName(
  root: DataFileMap | null,
  folderSegments: string[],
  fileName: string,
): DataFileMap | null {
  if (!root) return null
  let current = root
  const wantedFile = fileName.toLowerCase()
  for (const segment of folderSegments) {
    const wanted = segment.toLowerCase()
    const next = current.children.find((child) => child.isFolder && child.name.toLowerCase() === wanted)
    if (!next) return null
    current = next
  }
  return current.children.find((child) => !child.isFolder && child.name.toLowerCase() === wantedFile) ?? null
}

function findLeafByMeta(
  root: DataFileMap | null,
  mappingPath: string,
  relativePath: string,
): DataFileMap | null {
  if (!root) return null
  const mapping = mappingPath.trim().toLowerCase()
  const relative = relativePath.trim().toLowerCase()
  let hit: DataFileMap | null = null
  const walk = (node: DataFileMap): void => {
    if (hit) return
    if (!node.isFolder) {
      if (node.mappingPath.trim().toLowerCase() === mapping && node.relativePath.trim().toLowerCase() === relative) {
        hit = node
      }
      return
    }
    for (const child of node.children) walk(child)
  }
  walk(root)
  return hit
}

async function collectFiles(
  dir: FileSystemDirectoryHandle,
  diskSegments: string[],
  category: ResourceCategoryKey,
  out: DiskResourceFile[],
): Promise<void> {
  for await (const entry of dir.values()) {
    if (entry.name.startsWith('.')) continue
    if (entry.kind === 'directory') {
      await collectFiles(entry, [...diskSegments, entry.name], category, out)
      continue
    }
    if (!isAllowedFile(category, entry.name)) continue
    const file = await (entry as FileSystemFileHandle).getFile()
    out.push({
      category,
      folderSegments: resolveFolderSegments(category, diskSegments),
      fileName: entry.name,
      file,
    })
  }
}

/**
 * 扫描工程根目录下 Graphics / audio / Font，把本地文件补进虚拟资源树。
 * 已有 http(s) 地址的节点不重复上传。
 */
export async function syncLocalProjectResourcesToCloud(params: {
  projectRoot: FileSystemDirectoryHandle
  project: MainProject
  sessionId: string
  currentRoots: Record<ResourceCategoryKey, DataFileMap | null>
  onProgress?: (percent: number, detail: string) => void
}): Promise<Record<ResourceCategoryKey, DataFileMap | null>> {
  const guid = params.project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('工程缺少 guid，无法上传本地资源')
  }

  params.onProgress?.(0, '扫描本地资源…')
  const files: DiskResourceFile[] = []
  const graphicsDir = await tryGetDirectory(params.projectRoot, 'Graphics')
  if (graphicsDir) {
    for await (const entry of graphicsDir.values()) {
      if (entry.kind !== 'directory') continue
      if (entry.name.toLowerCase() === 'video') {
        await collectFiles(entry, [], 'video', files)
        continue
      }
      await collectFiles(entry, [entry.name], 'graphic', files)
    }
  }
  const audioDir = (await tryGetDirectory(params.projectRoot, 'audio'))
    ?? (await tryGetDirectory(params.projectRoot, 'Audio'))
  if (audioDir) {
    await collectFiles(audioDir, [], 'audio', files)
  }
  const fontDir = await tryGetDirectory(params.projectRoot, 'Font')
  if (fontDir) {
    await collectFiles(fontDir, [], 'font', files)
  }

  const roots: Record<ResourceCategoryKey, DataFileMap | null> = {
    graphic: params.currentRoots.graphic ?? buildDefaultVirtualResourceRoot('graphic'),
    audio: params.currentRoots.audio ?? buildDefaultVirtualResourceRoot('audio'),
    font: params.currentRoots.font ?? buildDefaultVirtualResourceRoot('font'),
    video: params.currentRoots.video ?? buildDefaultVirtualResourceRoot('video'),
  }
  for (const key of ['graphic', 'audio', 'font', 'video'] as const) {
    roots[key] = syncVirtualResourcePathMetadata(key, roots[key]!)
  }

  const pending = files.filter((item) => {
    const meta = resolveVirtualResourcePathMeta(item.category, item.folderSegments.join('/'), item.fileName)
    const existing = findLeafByName(roots[item.category], item.folderSegments, item.fileName)
      ?? findLeafByMeta(roots[item.category], meta.mappingPath, meta.relativePath)
    return !existing || !isRemoteUrl(existing.resourceUrl)
  })

  if (!pending.length) {
    params.onProgress?.(100, '本地资源已在云端')
    return roots
  }

  const version = String(
    params.project.projectHeader.Version || params.project.projectConstant.gameVersion || params.project.dataVer || 1,
  )
  params.onProgress?.(8, `上传 ${pending.length} 个本地资源…`)
  const uploaded = await uploadMaterialFiles({
    guid,
    sessionId: params.sessionId,
    version,
    items: pending.map((item) => ({
      file: item.file,
      objectName: buildProjectResourceObjectName({
        guid,
        category: item.category,
        folderSegments: item.folderSegments,
        fileName: item.fileName,
      }),
      resultFileName: item.fileName,
    })),
    onProgress: (_stage, percent) => {
      params.onProgress?.(8 + Math.round(percent * 0.86), `上传本地资源 ${percent}%`)
    },
  })

  for (let index = 0; index < pending.length; index += 1) {
    const item = pending[index]!
    const result = uploaded[index]!
    roots[item.category] = appendMaterialResourceMappingEntry({
      root: roots[item.category],
      category: item.category,
      folderSegments: item.folderSegments,
      fileName: item.fileName,
      url: result.url,
      md5: result.md5,
      size: result.size,
      replaceSameName: true,
    }).root
  }

  params.onProgress?.(100, `已同步 ${pending.length} 个本地资源`)
  return roots
}
